import React, { ReactNode } from "react";
import { useAccessControl } from "./AccessControlContext";

interface Props {
  pageKey: string;
  /** When provided, the element is bound to a configured element key. */
  elementKey?: string;
  /** Render this instead of nothing when access is denied. */
  fallback?: ReactNode;
  /**
   * When true, requires the current user to be able to admin this page
   * (Root, or listed in pages[pageKey].adminRoleIds).
   */
  adminOnly?: boolean;
  children: ReactNode;
}

/**
 * Conditionally renders children based on the element's role configuration.
 * Unconfigured elements are visible to everyone (permissive default).
 */
const ElementGuard: React.FC<Props> = ({
  pageKey,
  elementKey,
  fallback = null,
  adminOnly,
  children,
}) => {
  const { canSeeElement, canAdminPage, loaded } = useAccessControl();

  if (!loaded) return null;
  if (adminOnly && !canAdminPage(pageKey)) return <>{fallback}</>;
  if (elementKey && !canSeeElement(pageKey, elementKey)) return <>{fallback}</>;

  return <>{children}</>;
};

export default ElementGuard;
